import React, { createContext, useCallback, useEffect, useState } from 'react';
import { CognitoService } from './CognitoService';
import { cognitoConfig } from './config';
import type { AuthUser } from '@/types/auth';
import { useAuthStore } from '@/store/authStore';
import { configurePurchases, resetPurchasesUser } from '@/payments/purchases';
import { setAnalyticsUser, track } from '@/analytics/track';

const DEV_USER: AuthUser = {
  id: 'dev-user',
  email: 'dev@localhost',
  username: 'Developer',
  emailVerified: true,
};

const isDevMode = !cognitoConfig.userPoolId && !cognitoConfig.clientId;

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  signUp: (
    identifier: string,
    password: string,
    opts: { kind: 'email' | 'phone'; acceptedTerms: boolean },
  ) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(isDevMode ? DEV_USER : null);
  // isInitializing gates the app on the ONE-TIME session bootstrap. isLoading is for
  // in-screen operations (signUp/signIn/…) and drives button spinners only — it must
  // NOT gate the root, or toggling it mid-signup unmounts the nav Stack and loses the
  // verify step (the screen would remount and reset to sign-in).
  const [isInitializing, setIsInitializing] = useState(!isDevMode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDevMode) return;
    CognitoService.getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setIsInitializing(false));
  }, []);

  useEffect(() => {
    useAuthStore.getState().setUser(user);
    setAnalyticsUser(user?.id ?? null);
    // Identify the user to RevenueCat (app_user_id = Cognito sub) so a purchase's
    // webhook maps to USER#<sub>; clear it on sign-out. No-ops where the SDK isn't
    // available (editor preview / pre-rebuild dev client).
    if (user) void configurePurchases(user.id);
    else void resetPurchasesUser();
  }, [user]);

  const clearError = useCallback(() => setError(null), []);

  const signUp = useCallback(
    async (
      identifier: string,
      password: string,
      opts: { kind: 'email' | 'phone'; acceptedTerms: boolean },
    ) => {
      if (!opts.acceptedTerms) {
        throw new Error('You must accept the terms and conditions to create an account.');
      }
      setError(null);
      setIsLoading(true);
      try {
        const acceptedTermsAt = new Date().toISOString();
        await CognitoService.signUp(identifier, password, {
          kind: opts.kind,
          acceptedTermsAt,
        });
        track('signup');
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Sign up failed';
        setError(msg);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    setError(null);
    setIsLoading(true);
    try {
      await CognitoService.confirmSignUp(email, code);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Confirmation failed';
      setError(msg);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const u = await CognitoService.signIn(email, password);
      setUser(u);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sign in failed';
      setError(msg);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      await CognitoService.signOut();
      setUser(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sign out failed';
      setError(msg);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      await CognitoService.deleteUser();
      setUser(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Delete account failed';
      setError(msg);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    setError(null);
    setIsLoading(true);
    try {
      await CognitoService.forgotPassword(email);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to send reset email';
      setError(msg);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      setError(null);
      setIsLoading(true);
      try {
        await CognitoService.confirmForgotPassword(email, code, newPassword);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Password reset failed';
        setError(msg);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    isInitializing,
    error,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    deleteAccount,
    forgotPassword,
    confirmForgotPassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
